'use client';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useKeyboardInset } from '@hooks/useKeyboardInset';

import { Button } from '@components/Button';
import { BottomContent } from '@components/Common';

import {
  FullScreenContainer,
  FullScreenHeader,
  FullScreenTextareaField,
  FullScreenWrapper,
  HeaderTitle,
  ModalPageBottom,
} from './styles';

interface FullScreenTextareaProps {
  isOpen: boolean;
  value: string;
  label: string;
  placeholder: string;
  onClose: () => void;
  onSave: (value: string) => void;
}

export const FullScreenTextarea: React.FC<FullScreenTextareaProps> = ({
  isOpen,
  value,
  label,
  placeholder,
  onClose,
  onSave,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation('common');
  const keyboardInset = useKeyboardInset();
  const [viewportHeight, setViewportHeight] = useState<number>(
    typeof window !== 'undefined' ? (window.visualViewport?.height ?? window.innerHeight) : 0,
  );

  // Track visual viewport height for proper layout calculations
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const vv = window.visualViewport;
    const onResize = () => setViewportHeight(vv?.height ?? window.innerHeight);
    onResize();
    vv?.addEventListener('resize', onResize);
    window.addEventListener('resize', onResize);
    return () => {
      vv?.removeEventListener('resize', onResize);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // Update local value when prop changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Handle modal open/close effects
  useEffect(() => {
    if (!isOpen) return;

    // Prevent body scroll when modal is open
    const originalOverflow = document.body.style.overflow;
    const originalOverscroll = document.documentElement.style.overscrollBehavior;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overscrollBehavior = 'contain';

    // Focus and scroll to end with better mobile support
    const focusAndScroll = () => {
      if (!textareaRef.current) return;

      const textarea = textareaRef.current;
      textarea.focus();

      // Set cursor to end and scroll to bottom
      const length = localValue.length;
      textarea.setSelectionRange(length, length);
      // Force input event for mobile keyboard
      textarea.dispatchEvent(new Event('input', { bubbles: true }));
      textarea.scrollTop = textarea.scrollHeight;
    };

    // Multiple attempts for better mobile reliability
    focusAndScroll();
    const timeout1 = setTimeout(focusAndScroll, 100); // Initial delay for mobile keyboard to start showing
    const timeout2 = setTimeout(focusAndScroll, 300); // Additional delay for slower devices

    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      document.body.style.overflow = originalOverflow;
      document.documentElement.style.overscrollBehavior = originalOverscroll;
    };
  }, [isOpen, localValue.length]);

  // Maintain focus and scroll when keyboard changes
  useEffect(() => {
    if (!isOpen || !textareaRef.current) return;

    const textarea = textareaRef.current;
    const scrollToEnd = () => {
      // Keep cursor at end and scroll to bottom
      const length = textarea.value.length;
      textarea.setSelectionRange(length, length);
      textarea.scrollTop = textarea.scrollHeight;
    };

    // Small delay to let keyboard animation settle
    const timeout = setTimeout(scrollToEnd, 100);
    return () => clearTimeout(timeout);
  }, [isOpen, keyboardInset]);

  // Calculate available height for textarea (viewport minus header and button space)
  const headerHeight = headerRef.current?.offsetHeight ?? 80;
  const buttonHeight = buttonRef.current?.offsetHeight ?? 80;
  const availableHeight = Math.max(200, viewportHeight - headerHeight - buttonHeight - 20); // 20px for gaps

  const handleSave = () => {
    onSave(localValue);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <FullScreenContainer>
      <FullScreenWrapper>
        <FullScreenHeader ref={headerRef}>
          <HeaderTitle>{label}</HeaderTitle>
        </FullScreenHeader>

        <FullScreenTextareaField
          ref={textareaRef}
          value={localValue}
          placeholder={placeholder}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setLocalValue(e.target.value)}
          style={{ maxHeight: `${availableHeight}px` }}
        />

        <ModalPageBottom
          ref={buttonRef}
          style={{ bottom: `${keyboardInset}px` }}
          onTouchMove={(e) => e.preventDefault()}>
          <BottomContent>
            <Button onClick={handleSave} label={t('ready')} />
          </BottomContent>
        </ModalPageBottom>
      </FullScreenWrapper>
    </FullScreenContainer>
  );
};
